import { useEffect, useState } from 'react';
import { useReduxState } from 'hooks';
import { addMonths, format as formatDate } from 'date-fns';
import Mustache from 'mustache';
import { useApiData } from 'lib/api';
import { Button } from 'components/common';
import { Modal, ModalContent, ModalPortal } from 'components/common/modal';

interface Props {
  backgroundImage?: string;
  certificateType: 'course' | 'path' | 'calendar-event';
  content?: string;
  customContent?: string;
  data?: string;
  dateFormat?: string;
  disabled?: boolean;
  expirationTime?: number;
  format?: 'A4' | 'A5';
  landscape?: boolean;
  logo?: string;
  onError?: (message: string) => void;
  pathCourses?: { number: number; title: string }[];
  showExpirationDate?: boolean;
  showScore?: boolean;
  templateId?: number;
  title?: string;
}

const CertificatesPreview = ({
  backgroundImage,
  certificateType,
  content,
  customContent,
  data,
  dateFormat,
  disabled,
  expirationTime,
  format,
  landscape,
  logo,
  onError,
  pathCourses,
  showExpirationDate,
  showScore,
  templateId,
  title
}: Props) => {
  const { language, messages, serverConfig, theme } = useReduxState();
  const [html, setHtml] = useState('');
  const [modalVisible, setModalVisible] = useState(false);

  const { data: templateResponseData, loading } = useApiData({
    url: templateId
      ? `/admin/certificate-templates/${templateId}`
      : `/admin/certificate-built-in-template`
  });

  const { template, templateMessages } = templateResponseData || {};

  useEffect(() => {
    update();
  }, [
    backgroundImage,
    content,
    customContent,
    data,
    expirationTime,
    pathCourses,
    showExpirationDate,
    showScore,
    template,
    title
  ]);

  const hideModal = () => setModalVisible(false);

  const showModal = () => setModalVisible(true);

  const update = () => {
    try {
      const dateFormatValue = dateFormat || template.dateFormat;
      const logoValue = logo || (templateId ? template.logo : null);

      const variables = {
        customContent:
          'Lorem ipsum dolor sit amet, consectetuer adipiscing elit.' +
          ' Sed posuere interdum sem. Quisque ligula eros ullamcorper' +
          ' quis, lacinia quis facilisis sed sapien. Mauris varius diam vita',
        highlightColor: theme.certificateHighlightColor || '#245268',
        logo: logoValue || theme.logo,
        logoWidth: theme.certificateLogoWidth || 150,
        messages: templateMessages,
        showName:
          serverConfig.userUseName || serverConfig.userUseNameInCertificate,
        user: {
          id: 1,
          email:
            language === 'fi-FI'
              ? 'oiva.oppija@vuolearning.fi'
              : 'bob.smith@vuolearning.fi',
          username: language === 'fi-FI' ? 'oiva.oppija' : 'bob.smith',
          firstName: language === 'fi-FI' ? 'Oiva' : 'Bob',
          lastName: language === 'fi-FI' ? 'Oppija' : 'Smith',
          customAttributes: {}
        }
      };

      if (certificateType === 'course') {
        const courseVariables = {
          ...variables,
          completionDate: formatDate(Date.now(), dateFormatValue),
          course: {
            id: 1,
            title:
              title ||
              (language === 'fi-FI'
                ? 'GDPR: Kuinka Gepardi kesytetään?'
                : 'Carbon Footprint'),
            certificateBackgroundImage: backgroundImage,
            certificateShowExpirationDate:
              showExpirationDate !== undefined ? showExpirationDate : true,
            certificateShowScore: showScore !== undefined ? showScore : true,
            ...(customContent && { certificateCustomContent: customContent })
          },
          courseTitle:
            title ||
            (language === 'fi-FI'
              ? 'GDPR: Kuinka Gepardi kesytetään?'
              : 'Carbon Footprint'),
          expirationDate: formatDate(
            addMonths(new Date(), expirationTime || 6),
            dateFormatValue
          ),
          expirationTimeInMonths: expirationTime || 6,
          tasksPercentage: '79%',
          tasksPoints: '15/19'
        };

        const courseContent = Mustache.render(
          content || template.courseContent,
          courseVariables
        );
        const templateCourseHtml = Mustache.render(data || template.data, {
          ...courseVariables,
          content: courseContent
        });

        setHtml(templateCourseHtml);
      } else if (certificateType === 'path') {
        const defaults =
          language === 'fi-FI'
            ? {
                completedCourses: [
                  { number: 1, title: 'Johdatus myyntiin' },
                  {
                    number: 2,
                    title: 'Myynnin perusteet: Kuinka päästä alkuun'
                  },
                  { number: 3, title: 'Myynnin prosessit' },
                  { number: 4, title: 'Myynnin sanasto' }
                ],
                title: 'Myynnin opintopolku'
              }
            : {
                completedCourses: [
                  { number: 1, title: 'Introduction to Sales' },
                  { number: 2, title: 'Basics of Sales: How to get started' },
                  { number: 3, title: 'Sales processes' },
                  { number: 4, title: 'Sales terminology' }
                ],
                title: 'Sales path'
              };

        const pathVariables = {
          ...variables,
          path: {
            id: 1,
            title: title || defaults.title,
            ...(customContent && { certificateCustomContent: customContent })
          },
          pathTitle: title || defaults.title,
          completionDate: formatDate(Date.now(), dateFormatValue),
          completedCourses: pathCourses || defaults.completedCourses,
          ...(customContent && { customContent })
        };

        const pathContent = Mustache.render(
          content || template.pathContent,
          pathVariables
        );
        const templatePathHtml = Mustache.render(data || template.data, {
          ...pathVariables,
          content: pathContent
        });

        setHtml(templatePathHtml);
      } else if (certificateType === 'calendar-event') {
        const defaults =
          language === 'fi-FI'
            ? {
                calendarEventTitle: 'Vuolearning kesäseminaari 2025',
                eventStartDate: '12.08.2025',
                eventEndDate: '16.08.2025'
              }
            : {
                calendarEventTitle: 'Vuolearning Summer Seminar 2025',
                eventStartDate: '12.08.2025',
                eventEndDate: '16.08.2025'
              };

        const calendarEventVariables = {
          ...variables,
          calendarEvent: {
            id: 1,
            title: title || defaults.calendarEventTitle,
            ...(customContent && { certificateCustomContent: customContent })
          },
          calendarEventTitle: title || defaults.calendarEventTitle,
          eventStartDate: defaults.eventStartDate,
          eventEndDate: defaults.eventEndDate,
          ...(customContent && { customContent })
        };

        const calendarEventContent = Mustache.render(
          content || template.calendarEventContent,
          calendarEventVariables
        );
        const templateCalendarEventHtml = Mustache.render(
          data || template.data,
          {
            ...calendarEventVariables,
            content: calendarEventContent
          }
        );

        setHtml(templateCalendarEventHtml);
      }
    } catch (err) {
      setHtml(err.message);
      if (onError) onError(err.message);
    }
  };

  const templateFormat =
    format ||
    (certificateType === 'course'
      ? template?.courseFormat
      : certificateType === 'path'
      ? template?.pathFormat
      : template?.calendarEventFormat);

  const templateIsLandscape =
    landscape ||
    (certificateType === 'course'
      ? template?.courseLandscape
      : certificateType === 'path'
      ? template?.pathLandscape
      : template?.calendarEventLandscape);

  const size = {
    A4: {
      height: '297mm',
      width: '210mm'
    },
    A5: {
      height: '210mm',
      width: '148mm'
    }
  }[templateFormat];

  const maxWidth = {
    A4: templateIsLandscape ? 1153 : 824,
    A5: templateIsLandscape ? 824 : 589
  }[templateFormat];

  return (
    <>
      <Button
        category="light"
        disabled={disabled || loading}
        icon="eye"
        onClick={showModal}
        text={messages.commonShowPreview}
      />
      <ModalPortal>
        <Modal
          closeButton={true}
          onHide={hideModal}
          style={{ maxWidth }}
          title={messages.siteCertificatesCertificatePreview}
          visible={modalVisible}
        >
          <ModalContent>
            <div className="content">
              {loading ? null : (
                <iframe
                  srcDoc={html}
                  style={{
                    border: 0,
                    overflow: 'hidden',
                    margin: 'auto',
                    height: templateIsLandscape ? size?.width : size?.height,
                    width: templateIsLandscape ? size?.height : size?.width
                  }}
                />
              )}
            </div>
          </ModalContent>
        </Modal>
      </ModalPortal>
    </>
  );
};

export default CertificatesPreview;
